import { useCallback, useState, type ReactNode } from 'react';
import { materialName } from '../app/material.js';
import { api, ApiError } from '../shell/api.js';
import { useSpreadSize } from '../shell/spread-size.js';
import { useAsync } from '../shell/useAsync.js';

/**
 * What the composer is pointed at. There is one composer, and the selection decides
 * its scope: nothing selected directs the Experiment, a selected Candidate or slide
 * makes an immutable descendant inside that scope.
 *
 * `appId` travels with the two scopes that make a descendant, because R058 allows the
 * instruction to arrive with new references and the material an Experiment may name is
 * its own App's Library and no other App's.
 */
export type ComposerScope =
  | { readonly kind: 'experiment'; readonly experimentId: string; readonly brief: string }
  | { readonly kind: 'candidate'; readonly candidateId: string; readonly label: string; readonly appId: string }
  | { readonly kind: 'slide'; readonly itemId: string; readonly label: string; readonly appId: string };

export function scopeLabel(scope: ComposerScope): string {
  switch (scope.kind) {
    case 'experiment':
      return 'Направляю этот Experiment';
    case 'candidate':
      return `Направляю ${scope.label}`;
    case 'slide':
      return `Направляю ${scope.label}`;
  }
}

function actionLabel(scope: ComposerScope): string {
  return scope.kind === 'experiment' ? 'Explore это направление' : 'Сгенерировать изменение';
}

/**
 * One contextual composer, on every creative surface. Plain language in, a complete
 * descendant out; nothing here is a prompt, a weight or a model choice.
 */
export function Composer({ scope, onDone }: { scope: ComposerScope; onDone: () => void }): ReactNode {
  const spreadSize = useSpreadSize();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [materialIds, setMaterialIds] = useState<readonly string[]>([]);

  const submit = useCallback(async () => {
    const instruction = text.trim();
    if (instruction === '') return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (scope.kind === 'experiment') {
        // Directing the Experiment is a change to the Brief every later spread reads.
        const brief = scope.brief.trim() === '' ? instruction : `${scope.brief}\n${instruction}`;
        await api.updateExperiment(scope.experimentId, { brief });
        await api.spread(scope.experimentId, 'explore');
        // The operator's own Brief just changed. Editing their text silently, even to
        // carry out what they asked for, is still editing their text behind their back.
        setNotice(`Добавлено в Brief: «${instruction}». Ещё шесть Candidate уже в пути.`);
      } else if (scope.kind === 'candidate') {
        await api.branchCandidate(scope.candidateId, {
          instruction,
          ...(materialIds.length === 0 ? {} : { materialIds: [...materialIds] }),
        });
      } else {
        await api.editSlide(scope.itemId, {
          instruction,
          ...(materialIds.length === 0 ? {} : { materialIds: [...materialIds] }),
        });
      }
      setText('');
      setMaterialIds([]);
      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Это направление не удалось отправить.');
    } finally {
      setBusy(false);
    }
  }, [materialIds, onDone, scope, text]);

  return (
    <div className="composer">
      <p className="composer-scope">{scopeLabel(scope)}</p>
      {scope.kind !== 'experiment' ? null : (
        <p className="note" id="composer-hint">
          {spreadSize === 1
            ? 'Написанное здесь добавляется в Brief и запускает ещё один Candidate через подписку.'
            : 'Написанное здесь добавляется в Brief и запускает Explore ещё шести Candidate.'}
        </p>
      )}
      <label htmlFor="composer-input">Креативное направление</label>
      <input
        id="composer-input"
        aria-describedby={scope.kind === 'experiment' ? 'composer-hint' : undefined}
        value={text}
        placeholder="Скажите своими словами, что должно измениться"
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') void submit();
        }}
      />
      {scope.kind === 'experiment' ? null : (
        <NewReferences
          appId={scope.appId}
          chosen={materialIds}
          onToggle={(id) =>
            setMaterialIds((held) => (held.includes(id) ? held.filter((one) => one !== id) : [...held, id]))
          }
        />
      )}
      <button className="primary" type="button" disabled={busy || text.trim() === ''} onClick={() => void submit()}>
        {busy ? 'Отправляю…' : actionLabel(scope)}
      </button>
      {error === null ? null : (
        <p className="failure" role="alert">
          {error}
        </p>
      )}
      {notice === null ? null : (
        <p className="note" role="status">
          {notice}
        </p>
      )}
    </div>
  );
}

/**
 * R058: the optional half of a branch instruction.
 *
 * One sentence is the whole requirement, and naming a piece of this App's material
 * beside it is the other half of it - the server has taken `materialIds` on a branch and
 * on a slide edit since task 07, and nothing sent one. There is no weight here and no
 * lock: a reference either travels with the instruction or it does not.
 */
function NewReferences({
  appId,
  chosen,
  onToggle,
}: {
  appId: string;
  chosen: readonly string[];
  onToggle: (libraryItemId: string) => void;
}): ReactNode {
  const library = useAsync(async () => api.readLibrary(appId), [appId]);
  const items = library.value?.items ?? [];
  if (items.length === 0) return null;

  return (
    <details className="secondary-action">
      <summary>{`Добавить ссылки · выбрано ${chosen.length}`}</summary>
      <p className="note">Материал из Library этого App, уходит вместе с инструкцией. Больше ничего не передаётся.</p>
      <ul className="library-choices" aria-label="Ссылки для этого изменения">
        {items.map((item) => {
          const name = materialName(item);
          const taken = chosen.includes(item.id);
          return (
            <li key={item.id}>
              <span>{`${name} · ${item.role}`}</span>
              <button type="button" className="ghost" aria-pressed={taken} onClick={() => onToggle(item.id)}>
                {taken ? `Убрать ${name} как ссылку` : `Добавить ${name} как ссылку`}
              </button>
            </li>
          );
        })}
      </ul>
    </details>
  );
}
